import { apiClient } from './client';

// Types
export interface Plan {
  id: string;
  code: string;
  title: string;
  duration_value: number;
  duration_unit: 'week' | 'month' | 'year';
  price: number;
  old_price: number | null;
  currency: string;
  is_active: boolean;
  metadata: Record<string, any> | null;
  /** Ценовой вариант, которому принадлежит тариф. null — базовый прайс-лист. */
  variant_id: string | null;
  variant?: { id: string; name: string; status: string } | null;
  created_at: string;
}

export interface CreatePlanDto {
  code: string;
  title: string;
  duration_value: number;
  duration_unit: 'week' | 'month' | 'year';
  price: number;
  old_price?: number;
  currency?: string;
  is_active?: boolean;
  metadata?: Record<string, any>;
  variant_id?: string | null;
}

export interface UpdatePlanDto extends Partial<CreatePlanDto> {}

export interface Order {
  id: string;
  user_id: string;
  plan_id: string;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'cancelled' | 'refunded';
  promo_code_id: string | null;
  created_at: string;
  paid_at: string | null;
  plan?: Plan;
  user?: {
    id: string;
    username: string;
    email: string | null;
  };
  promo_code?: {
    id: string;
    code: string;
  };
}

export interface Payment {
  id: string;
  order_id: string;
  provider: 'click' | 'payme' | 'paylov';
  provider_payment_id: string | null;
  amount: number;
  currency: string;
  status: 'initiated' | 'success' | 'failed' | 'expired';
  raw_response: Record<string, any> | null;
  created_at: string;
  order?: Order;
}

export interface Subscription {
  id: string;
  user_id: string;
  order_id: string;
  starts_at: string;
  ends_at: string;
  status: 'active' | 'expired' | 'cancelled';
  created_at: string;
  order?: Order;
  user?: {
    id: string;
    username: string;
    email: string | null;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetOrdersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface GetPaymentsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  provider?: string;
}

export interface GetSubscriptionsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface PromoCode {
  id: string;
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  max_uses: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreatePromoCodeDto {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  max_uses?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
}

export interface UpdatePromoCodeDto extends Partial<CreatePromoCodeDto> {}

// Plans API
export const plansApi = {
  getAll: async (includeInactive?: boolean): Promise<Plan[]> => {
    const response = await apiClient.get<Plan[]>('/plans', {
      params: { includeInactive: includeInactive ? 'true' : 'false' },
    });
    return response.data;
  },

  getById: async (id: string): Promise<Plan> => {
    const response = await apiClient.get<Plan>(`/plans/${id}`);
    return response.data;
  },

  create: async (dto: CreatePlanDto): Promise<Plan> => {
    const response = await apiClient.post<Plan>('/plans', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdatePlanDto): Promise<Plan> => {
    const response = await apiClient.patch<Plan>(`/plans/${id}`, dto);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/plans/${id}`);
  },
};

// Orders API
export const ordersApi = {
  getAll: async (params: GetOrdersParams = {}): Promise<PaginatedResponse<Order>> => {
    const response = await apiClient.get<PaginatedResponse<Order>>('/orders', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Order> => {
    const response = await apiClient.get<Order>(`/orders/${id}`);
    return response.data;
  },
};

// Payments API
export const paymentsApi = {
  getAll: async (params: GetPaymentsParams = {}): Promise<PaginatedResponse<Payment>> => {
    const response = await apiClient.get<PaginatedResponse<Payment>>('/payments', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Payment> => {
    const response = await apiClient.get<Payment>(`/payments/${id}`);
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsApi = {
  getAll: async (params: GetSubscriptionsParams = {}): Promise<PaginatedResponse<Subscription>> => {
    const response = await apiClient.get<PaginatedResponse<Subscription>>('/subscriptions', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Subscription> => {
    const response = await apiClient.get<Subscription>(`/subscriptions/${id}`);
    return response.data;
  },
};

// Promo Codes API
export const promoCodesApi = {
  getAll: async (includeInactive?: boolean): Promise<PromoCode[]> => {
    const response = await apiClient.get<PromoCode[]>('/promo-codes', {
      params: { includeInactive: includeInactive ? 'true' : 'false' },
    });
    return response.data;
  },

  getById: async (id: string): Promise<PromoCode> => {
    const response = await apiClient.get<PromoCode>(`/promo-codes/${id}`);
    return response.data;
  },

  create: async (dto: CreatePromoCodeDto): Promise<PromoCode> => {
    const response = await apiClient.post<PromoCode>('/promo-codes', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdatePromoCodeDto): Promise<PromoCode> => {
    const response = await apiClient.patch<PromoCode>(`/promo-codes/${id}`, dto);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/promo-codes/${id}`);
  },
};

